import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, IndexTable, Modal, Select, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminPagedResult, AdminWebhookLog } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const pageSize = 50;

export default function AdminWebhookLogsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<AdminPagedResult<AdminWebhookLog> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPayload, setSelectedPayload] = useState<{ title: string; value: string } | null>(null);

  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);
  const topic = searchParams.get("topic") || "";
  const shopIdValue = searchParams.get("shopId") || "";
  const status = searchParams.get("status") || "all";

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    const parsedShopId = shopIdValue ? Number(shopIdValue) : undefined;
    const processedSuccessfully = status === "processed" ? true : status === "failed" ? false : undefined;

    try {
      const response = await agent.Admin.webhookLogs(
        token,
        page,
        pageSize,
        Number.isFinite(parsedShopId) ? parsedShopId : undefined,
        topic || undefined,
        processedSuccessfully
      );
      setResult(response);
    } catch {
      setError("Unable to load webhook logs.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, page, shopIdValue, topic, status, onUnauthorized]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = result ? Math.max(1, Math.ceil(result.totalCount / result.pageSize)) : 1;

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next);
  };

  const updateFilter = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    next.set("page", "1");
    setSearchParams(next);
  };

  const topicSuggestions = useMemo(() => {
    const topics = new Set((result?.items || []).map((x) => x.topic).filter((x): x is string => Boolean(x)));
    return Array.from(topics).sort();
  }, [result]);

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text as="h3" variant="headingMd">Webhook log diagnostics</Text>
          <Button onClick={loadLogs} loading={loading}>Refresh logs</Button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 180px 180px", gap: "0.75rem" }}>
          <TextField
            label="Filter by topic"
            value={topic}
            onChange={(value) => updateFilter({ topic: value })}
            autoComplete="off"
            placeholder="products/update"
          />
          <TextField
            label="Filter by Shop ID"
            value={shopIdValue}
            onChange={(value) => updateFilter({ shopId: value })}
            autoComplete="off"
            placeholder="123"
          />
          <Select
            label="Processing status"
            options={[
              { label: "All", value: "all" },
              { label: "Processed", value: "processed" },
              { label: "Failed", value: "failed" }
            ]}
            value={status}
            onChange={(value) => updateFilter({ status: value === "all" ? "" : value })}
          />
        </div>
        {topicSuggestions.length > 0 ? (
          <Text as="p" tone="subdued">Visible topics: {topicSuggestions.join(", ")}</Text>
        ) : null}
        {error ? <Text as="p" tone="critical">{error}</Text> : null}
        {loading ? (
          <Text as="p">Loading webhook logs...</Text>
        ) : !result || result.items.length === 0 ? (
          <Text as="p">No webhook logs found.</Text>
        ) : (
          <IndexTable
            resourceName={{ singular: "webhook log", plural: "webhook logs" }}
            itemCount={result.items.length}
            selectable={false}
            headings={[
              { title: "ID" },
              { title: "Store" },
              { title: "Topic" },
              { title: "Received" },
              { title: "Processed" },
              { title: "Ingested" },
              { title: "Pushed" },
              { title: "Payload" }
            ]}
          >
            {result.items.map((row, index) => (
              <IndexTable.Row id={`${row.id}`} key={row.id} position={index}>
                <IndexTable.Cell>{row.id}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Button variant="plain" onClick={() => navigate(`/pf-console/stores/${row.shopId}`)}>{row.shopName || row.shopDomain || `Shop ${row.shopId}`}</Button>
                </IndexTable.Cell>
                <IndexTable.Cell>{row.topic || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.receivedAt)}</IndexTable.Cell>
                <IndexTable.Cell>
                  {row.processedSuccessfully ? <Badge tone="success">Yes</Badge> : <Badge tone="critical">No</Badge>}
                </IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.ingestedAt)}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.pushedAt)}</IndexTable.Cell>
                <IndexTable.Cell>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    <Button variant="plain" onClick={() => setSelectedPayload({ title: `Webhook ${row.id} body`, value: row.requestBody || "(empty body)" })}>Body</Button>
                    <Button variant="plain" onClick={() => setSelectedPayload({ title: `Webhook ${row.id} headers`, value: row.headers || "(empty headers)" })}>Headers</Button>
                  </div>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}

        <Modal
          open={selectedPayload !== null}
          onClose={() => setSelectedPayload(null)}
          title={selectedPayload?.title || "Webhook detail"}
          primaryAction={{ content: "Close", onAction: () => setSelectedPayload(null) }}
        >
          <Modal.Section>
            <Text as="p" breakWord>{selectedPayload?.value || "(empty)"}</Text>
          </Modal.Section>
        </Modal>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Previous</Button>
          <Text as="span">Page {page} of {totalPages}</Text>
          <Button disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </Card>
  );
}
